/* lf-collection — instant-apply filter pills, sort and pagination.

   Every control is a working link or form on its own (pills navigate, the sort
   form submits, pagination links load a page). This file upgrades that to a
   Section Rendering API swap: fetch the target URL with ?section_id=, replace
   [data-lf-swap] with the fetched one, and push the URL so back / forward and
   sharing keep working.

   Because the whole swap region is replaced, every handler is delegated from
   the root — nothing needs re-binding after a swap. State that must survive a
   swap (the mobile pill disclosure) lives as an attribute on the root, which
   sits outside the swapped region. */
(function () {
  var ROOT = '[data-lf-collection]';
  var SWAP = '[data-lf-swap]';
  var PANEL = '[data-lf-facet-panel]';

  var reduceQuery = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : null;

  function reduced() {
    return !!(reduceQuery && reduceQuery.matches);
  }

  function init(root) {
    if (root.dataset.lfReady) return;
    root.dataset.lfReady = 'true';

    var sectionId = root.getAttribute('data-section-id');
    var live = root.querySelector('[data-lf-live]');
    var basePath = window.location.pathname;
    var request = null;

    function sectionUrl(url) {
      var next = new URL(url, window.location.href);
      next.searchParams.set('section_id', sectionId);
      return next.toString();
    }

    /* The product count lives in lf-page-title, a separate section, so a
       filtered swap would leave it stale. Guarded: the meta line is absent on
       mobile and on any template without the title section. */
    function syncCount() {
      var source = root.querySelector('[data-lf-count]');
      if (!source) return;
      var text = source.textContent.trim();
      var targets = document.querySelectorAll('.lf-page-title__meta');
      for (var i = 0; i < targets.length; i++) {
        targets[i].textContent = text;
      }
      if (live) live.textContent = text;
    }

    /* The mobile pill column animates open and closed on max-height. The open
       height is measured here and handed to the CSS as --lf-pills-h; only the
       mobile media query reads that property, so the desktop row — which never
       has a max-height — cannot be collapsed by a value left behind on resize.

       Everything else about the panel (tab order, the a11y tree, the reduced
       motion opt-out) is CSS: `visibility` carries the closed state and its
       transition is what waits out the collapse. */
    function openFacets(toggle, panel) {
      // Measured before the open attribute lands: reading scrollHeight forces a
      // style flush, and doing that with the panel already open would leave the
      // transition starting from the CSS fallback height rather than from 0.
      if (panel) panel.style.setProperty('--lf-pills-h', panel.scrollHeight + 'px');

      root.setAttribute('data-facets-open', '');
      if (toggle) toggle.setAttribute('aria-expanded', 'true');

      // Nothing will transition, so nothing will fire transitionend to settle it.
      if (panel && reduced()) panel.setAttribute('data-lf-panel-settled', '');
    }

    function closeFacets(toggle, panel) {
      if (panel) {
        // Released to `none` while open, so pin the measured height before
        // collapsing or the transition has nothing to animate from.
        panel.style.setProperty('--lf-pills-h', panel.scrollHeight + 'px');
        panel.removeAttribute('data-lf-panel-settled');
        void panel.offsetHeight;

        // The panel is about to leave the a11y tree; hand focus back to the
        // trigger rather than letting it drop to <body>.
        if (panel.contains(document.activeElement) && toggle) toggle.focus();
      }

      root.removeAttribute('data-facets-open');
      if (toggle) toggle.setAttribute('aria-expanded', 'false');
    }

    /* After a swap the panel is a brand new element: it has no measured height
       and has not transitioned, so an open row lands settled rather than
       replaying the reveal. */
    function restoreDisclosure() {
      var toggle = root.querySelector('[data-lf-facet-toggle]');
      var panel = root.querySelector(PANEL);
      var open = root.hasAttribute('data-facets-open');

      if (toggle) toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
      if (panel && open) panel.setAttribute('data-lf-panel-settled', '');
    }

    function navigate(url, options) {
      var opts = options || {};
      var swap = root.querySelector(SWAP);
      if (!swap) return;

      if (request) request.abort();
      request = new AbortController();
      root.setAttribute('data-lf-loading', '');

      fetch(sectionUrl(url), {
        signal: request.signal,
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
      })
        .then(function (response) {
          if (!response.ok) throw new Error(response.status);
          return response.text();
        })
        .then(function (html) {
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var next = doc.querySelector(SWAP);
          if (!next) throw new Error('no swap region');

          swap.innerHTML = next.innerHTML;

          if (opts.push !== false && window.history && window.history.pushState) {
            window.history.pushState({ lfCollection: true }, '', url);
          }

          restoreDisclosure();
          syncCount();

          if (opts.scroll) {
            root.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        })
        .catch(function (error) {
          /* An aborted request is a newer click winning, not a failure. A real
             failure falls back to a full page load so the click still works. */
          if (error && error.name === 'AbortError') return;
          window.location.assign(url);
        })
        .finally(function () {
          request = null;
          root.removeAttribute('data-lf-loading');
        });
    }

    root.addEventListener('click', function (event) {
      if (!(event.target instanceof Element)) return;

      var toggle = event.target.closest('[data-lf-facet-toggle]');
      if (toggle && root.contains(toggle)) {
        var panel = root.querySelector(PANEL);
        if (root.hasAttribute('data-facets-open')) closeFacets(toggle, panel);
        else openFacets(toggle, panel);
        return;
      }

      var pill = event.target.closest('[data-lf-pill]');
      if (pill && root.contains(pill) && pill.getAttribute('href')) {
        event.preventDefault();
        navigate(pill.getAttribute('href'), {});
        return;
      }

      var page = event.target.closest('.lf-pagination a[href]');
      if (page && root.contains(page)) {
        event.preventDefault();
        navigate(page.getAttribute('href'), { scroll: true });
      }
    });

    root.addEventListener('change', function (event) {
      if (!(event.target instanceof Element)) return;

      var sort = event.target.closest('[data-lf-sort]');
      if (!sort || !root.contains(sort)) return;

      var form = sort.closest('form');
      if (!form) return;

      /* The sort bar lives outside the swap region, so its width sizer has to
         follow the new choice here or the caret drifts until the next load. */
      var sizer = form.querySelector('[data-lf-sort-sizer]');
      var chosen = sort.options && sort.options[sort.selectedIndex];
      if (sizer && chosen) sizer.textContent = chosen.textContent.trim();

      /* Same URL the form would have submitted, built from its own fields, so
         the active filter hidden inputs ride along with the new sort. */
      var params = new URLSearchParams(new FormData(form));
      var action = form.getAttribute('action') || window.location.pathname;
      var query = params.toString();
      navigate(query ? action + '?' + query : action, {});
    });

    /* Delegated, so a panel swapped in by a filter change is covered without
       re-binding. Only the open transition settles the panel — the closing one
       ends with the row collapsed, which is the state the CSS already holds. */
    root.addEventListener('transitionend', function (event) {
      if (event.propertyName !== 'max-height') return;
      var panel = event.target;
      if (!panel || !panel.matches || !panel.matches(PANEL)) return;
      if (root.hasAttribute('data-facets-open')) panel.setAttribute('data-lf-panel-settled', '');
    });

    /* Only entries this section pushed are swapped in place. Anything else in
       history is a different page and must load normally. */
    window.addEventListener('popstate', function () {
      if (window.location.pathname !== basePath) return;
      navigate(window.location.href, { push: false });
    });
  }

  function boot(scope) {
    var roots = (scope || document).querySelectorAll(ROOT);
    for (var i = 0; i < roots.length; i++) init(roots[i]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      boot(document);
    });
  } else {
    boot(document);
  }

  document.addEventListener('shopify:section:load', function (event) {
    boot(event.target);
  });
})();
